<template>
  <div class="newsflash-page">
    <div class="informe-main-container">
      <div class="informe-header">
        <div class="newsflash-section-kicker">
          <span class="material-icons-outlined">bolt</span>
          Actualidad de mercado
        </div>
        <h1 class="typo-headline-4">{{ headerTitle }}</h1>
        <p class="typo-body-2">{{ headerDescription }}</p>
      </div>

      <div class="newsflash-content-single">
        <main class="main-column main-column-full">
          <section v-if="pageLoading" class="content-state">
            <div class="loading-spinner loading-spinner-large"></div>
            <h2>Cargando noticias flash</h2>
            <p>Estamos preparando el contenido.</p>
          </section>

          <section v-else-if="blockingError" class="content-state">
            <span class="material-icons-outlined state-icon">error</span>
            <h2>No se pudo cargar el contenido</h2>
            <p>{{ blockingError }}</p>
            <button type="button" class="read-more-button" @click="reloadCurrentView">
              Reintentar
            </button>
          </section>

          <section v-else-if="isDetailView && currentItem" class="newsflash-detail">
            <article class="detail-card">
              <div class="detail-hero">
                <div class="detail-topbar">
                  <div class="detail-topline">
                    <span class="meta-chip">{{ formatPublicationDate(currentItem.publication_date) }}</span>
                    <span v-if="currentItem.news_type_label" class="scope-chip">{{ currentItem.news_type_label }}</span>
                  </div>

                  <button
                    type="button"
                    class="read-more-button secondary-read-button"
                    @click="goToList"
                  >
                    <span class="material-icons-outlined">arrow_back</span>
                    Volver al listado
                  </button>
                </div>

                <h2 class="detail-headline">{{ currentItem.title }}</h2>
              </div>

              <div class="detail-block">
                <h2 class="detail-title">Que ha pasado</h2>
                <p class="detail-text">{{ currentItem.what_happened }}</p>
              </div>

              <div class="detail-block">
                <h2 class="detail-title">Consecuencias economicas</h2>
                <p class="detail-text">{{ currentItem.economic_consequences }}</p>
              </div>

              <div v-if="hasMarketContext(currentItem)" class="detail-block">
                <h2 class="detail-title">Lectura de mercado</h2>

                <div class="market-context-grid">
                  <div v-if="currentItem.news_type_label">
                    <span>Ambito</span>
                    <strong>{{ currentItem.news_type_label }}</strong>
                  </div>
                  <div v-if="currentItem.sector">
                    <span>Sector</span>
                    <strong>{{ currentItem.sector }}</strong>
                  </div>
                  <div v-if="currentItem.volatility_percent">
                    <span>Volatilidad estimada</span>
                    <strong>{{ formatVolatility(currentItem.volatility_percent) }}</strong>
                  </div>
                  <div v-if="currentItem.historical_high_ticker">
                    <span>Valor en zona historica</span>
                    <strong>{{ formatHistoricalExtremeTicker(currentItem.historical_high_ticker) }}</strong>
                  </div>
                </div>

                <p v-if="currentItem.volatility_reason" class="detail-text market-context-text">
                  {{ currentItem.volatility_reason }}
                </p>

                <div v-if="currentItem.comparable_event" class="comparable-event">
                  <span>Suceso comparable</span>
                  <strong>{{ currentItem.comparable_event.fecha }} · {{ currentItem.comparable_event.activo_o_mercado_afectado }}</strong>
                  <p>{{ currentItem.comparable_event.hecho }}</p>
                  <p>{{ currentItem.comparable_event.movimiento_observado }}</p>
                  <a v-if="currentItem.comparable_event.fuente_url" :href="currentItem.comparable_event.fuente_url" target="_blank" rel="noopener">
                    Fuente del suceso
                  </a>
                </div>
              </div>

              <div class="detail-block">
                <div class="detail-block-header">
                  <h2 class="detail-title">Valores relacionados</h2>
                </div>

                <div v-if="currentItem.affected_stocks_count === 0" class="empty-placeholder">
                  No hay valores relacionados registrados para esta noticia.
                </div>

                <div v-else class="stocks-list">
                  <article
                    v-for="stock in currentItem.affected_stocks"
                    :key="`${currentItem.slug}-${stock.ticker}`"
                    class="stock-item"
                  >
                    <div class="stock-item-header">
                      <span class="stock-ticker">{{ stock.ticker }}</span>
                      <span class="stock-name">{{ stock.nombre_empresa }}</span>
                    </div>

                    <div v-if="stock.a_que_se_dedica" class="stock-detail-group">
                      <p class="stock-detail-label">A qué se dedica</p>
                      <p class="stock-reason">{{ stock.a_que_se_dedica }}</p>
                    </div>

                    <div class="stock-detail-group">
                      <p class="stock-detail-label">Cómo se ve afectado</p>
                      <p class="stock-reason">{{ stock.como_se_ve_afectado }}</p>
                    </div>
                  </article>
                </div>
              </div>

              <footer class="detail-disclaimer">
                Esta información tiene finalidad exclusivamente informativa y educativa. No constituye asesoramiento financiero, recomendación de inversión ni invitación a comprar, vender o mantener instrumentos financieros. Cualquier decisión de inversión debe basarse en el análisis propio del usuario.
              </footer>
            </article>
          </section>

          <section v-else class="newsflash-list">
            <div v-if="!items.length" class="content-state">
              <span class="material-icons-outlined state-icon">newspaper</span>
              <h2>No hay noticias flash publicadas</h2>
              <p>Cuando se aprueben nuevas noticias aparecerán aquí.</p>
            </div>

            <template v-else>
              <article
                v-for="item in items"
                :key="item.id"
                class="informe-item newsflash-item"
              >
                <router-link :to="buildItemPath(item.slug)" class="newsflash-item-shell newsflash-item-link">
                  <div class="newsflash-item-header">
                    <div class="item-chip-row">
                      <span class="meta-chip">{{ formatPublicationDate(item.publication_date) }}</span>
                      <span v-if="item.news_type_label" class="scope-chip">{{ item.news_type_label }}</span>
                    </div>
                  </div>

                  <h2 class="informe-title newsflash-title-link">
                    {{ item.title }}
                  </h2>

                  <p class="newsflash-summary">{{ item.summary }}</p>

                  <div class="newsflash-card-footer">
                    <div v-if="item.affected_stocks_count" class="ticker-strip">
                      <span
                        v-for="stock in firstStocks(item)"
                        :key="`${item.slug}-${stock.ticker}`"
                        class="ticker-chip"
                      >
                        {{ stock.ticker }}
                      </span>
                      <span v-if="remainingStocksCount(item)" class="ticker-chip ticker-chip-muted">
                        +{{ remainingStocksCount(item) }}
                      </span>
                    </div>

                    <span class="newsflash-cta">
                      Leer noticia
                      <span class="material-icons-outlined">arrow_forward</span>
                    </span>
                  </div>
                </router-link>
              </article>
            </template>
          </section>
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import APICaller from '@/shared/mixins/APICaller.js';

export default {
  name: 'NoticiasFlash',
  mixins: [APICaller],
  data() {
    return {
      items: [],
      currentItem: null,
      listLoading: false,
      detailLoading: false,
      listError: '',
      detailError: '',
    };
  },
  computed: {
    isDetailView() {
      return Boolean(this.$route.params.slug);
    },
    pageLoading() {
      if (this.isDetailView) {
        return this.detailLoading && !this.currentItem;
      }

      return this.listLoading && !this.items.length;
    },
    blockingError() {
      if (this.isDetailView) {
        return this.currentItem ? '' : this.detailError;
      }

      return this.items.length ? '' : this.listError;
    },
    headerTitle() {
      return 'Noticias flash';
    },
    headerDescription() {
      if (this.isDetailView && this.currentItem) {
        return 'Lectura rapida del hecho, las consecuencias economicas y los valores afectados.';
      }

      return 'Movimientos recientes con repercusión en el ámbito económico.';
    },
  },
  watch: {
    '$route.params.slug'() {
      this.loadViewData();
    },
  },
  mounted() {
    this.loadViewData();
  },
  methods: {
    loadViewData() {
      window.scrollTo(0, 0);
      this.fetchList();

      if (this.isDetailView) {
        this.fetchItem();
      }
      else {
        this.currentItem = null;
        this.detailError = '';
        this.detailLoading = false;
      }
    },
    fetchList() {
      this.listLoading = true;
      this.listError = '';

      const successHandler = new APICaller.SuccessHandler(response => {
        this.items = Array.isArray(response.data) ? response.data : [];
        this.listLoading = false;
      });

      const failureHandler = new APICaller.FailureHandler('400', 'Error al cargar las noticias flash', error => {
        this.listError = this.resolveErrorMessage(error, 'No se pudo cargar el listado de noticias flash.');
        this.listLoading = false;
      });

      this._getAPICall('/api/newsflash-ai/published', successHandler, failureHandler);
    },
    fetchItem() {
      const slug = this.$route.params.slug;

      this.detailLoading = true;
      this.detailError = '';
      this.currentItem = null;

      const successHandler = new APICaller.SuccessHandler(response => {
        this.currentItem = response.data || null;
        this.detailLoading = false;
      });

      const failureHandler = new APICaller.FailureHandler('404', 'Error al cargar la noticia flash', error => {
        this.detailError = this.resolveErrorMessage(error, 'La noticia flash solicitada no existe o ya no está disponible.');
        this.detailLoading = false;
      });

      this._getAPICall(`/api/newsflash-ai/published/${encodeURIComponent(slug)}`, successHandler, failureHandler);
    },
    reloadCurrentView() {
      this.loadViewData();
    },
    goToList() {
      this.$router.push('/app/noticias-flash');
    },
    buildItemPath(slug) {
      return `/app/noticias-flash/${encodeURIComponent(slug)}`;
    },
    firstStocks(item) {
      const stocks = item && Array.isArray(item.affected_stocks) ? item.affected_stocks : [];
      return stocks.slice(0, 3);
    },
    remainingStocksCount(item) {
      const count = item && item.affected_stocks_count ? Number(item.affected_stocks_count) : 0;
      return Math.max(0, count - 3);
    },
    hasMarketContext(item) {
      return Boolean(item && (
        item.news_type_label ||
        item.sector ||
        item.volatility_percent ||
        item.volatility_reason ||
        item.comparable_event ||
        item.historical_high_ticker
      ));
    },
    formatVolatility(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) {
        return '-';
      }

      return `${new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(number)}%`;
    },
    formatHistoricalExtremeTicker(ticker) {
      if (!ticker) {
        return '-';
      }

      const extreme = ticker.tipo_extremo_label || 'Zona historica';
      const days = Number.isFinite(Number(ticker.dias_desde_extremo)) ? `, hace ${ticker.dias_desde_extremo} dias` : '';
      const date = ticker.fecha_extremo_historico ? `, ${ticker.fecha_extremo_historico}` : '';
      return `${ticker.ticker} (${extreme}${days}${date})`;
    },
    formatPublicationDate(value) {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return value;
      }

      return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(date);
    },
    resolveErrorMessage(error, fallback) {
      const response = error && error.response ? error.response : null;

      if (response && response.status === 404) {
        return 'La noticia flash solicitada no existe.';
      }

      if (response && response.data && typeof response.data.message === 'string') {
        return response.data.message;
      }

      return fallback;
    },
  },
  metaInfo() {
    const detailTitle = this.currentItem && this.currentItem.title
      ? `${this.currentItem.title} | Noticias flash`
      : 'Noticias flash | Eurekers';

    return {
      title: detailTitle,
    };
  },
};
</script>

<style lang="scss" scoped>
.newsflash-page {
  min-height: 100%;
  background: var(--color-background);
}

.informe-main-container {
  max-width: 1120px;
  margin: 0 auto;
  padding: 28px 24px 40px;
}

.informe-header {
  display: grid;
  gap: 10px;
  margin-bottom: 28px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--color-outline);

  h1 {
    margin: 0;
    color: var(--color-primary);
    font-weight: 600;
  }

  p {
    max-width: 720px;
    margin: 0;
    color: var(--color-text-secondary);
    line-height: 1.55;
  }
}

.newsflash-section-kicker {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  gap: 8px;
  color: var(--color-highlight);
  font-size: 13px;
  font-weight: 700;

  .material-icons-outlined {
    font-size: 18px;
  }
}

.main-column {
  width: 100%;
}

.main-column-full {
  max-width: 920px;
  margin: 0 auto;
}

.empty-placeholder,
.detail-text,
.stock-reason,
.content-state p {
  color: var(--color-text-secondary);
  line-height: 1.6;
}

.content-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.content-state {
  min-height: 320px;
  padding: 32px;
  border: 1px solid var(--color-outline);
  border-radius: 8px;
  background-color: var(--color-surface);
  text-align: center;

  h2 {
    margin: 0;
    color: var(--color-primary);
  }
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid var(--color-outline);
  border-top-color: var(--color-highlight);
  animation: spin 1s linear infinite;
}

.loading-spinner-large {
  width: 40px;
  height: 40px;
  border-width: 3px;
}

.state-icon {
  font-size: 34px;
  color: var(--color-error);
}

.newsflash-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.newsflash-item {
  background: var(--color-surface);
  border: 1px solid var(--color-outline);
  border-left: 4px solid var(--color-highlight);
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.3s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: var(--color-highlight);
    box-shadow: 0 10px 22px rgba(15, 15, 26, 0.08);
  }
}

.newsflash-item-shell {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 22px 24px;
}

.newsflash-item-link {
  color: inherit;
  text-decoration: none;
}

.detail-topline,
.detail-block-header,
.stock-item-header,
.newsflash-item-header {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
}

.item-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.meta-chip,
.scope-chip,
.ticker-chip,
.stock-ticker {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
}

.meta-chip,
.ticker-chip,
.stock-ticker {
  background-color: var(--color-active-surface);
  color: var(--color-text-primary);
}

.scope-chip {
  background: rgba(0, 173, 217, 0.12);
  color: var(--color-highlight);
  border: 1px solid rgba(0, 173, 217, 0.24);
}

.informe-title {
  margin: 0;
  color: var(--color-primary);
  font-size: 22px;
  font-weight: 600;
  line-height: 1.35;
}

.newsflash-title-link {
  text-decoration: none;
}

.newsflash-item-link:hover .newsflash-title-link {
  text-decoration: underline;
}

.newsflash-summary {
  margin: 0;
  color: var(--color-text-secondary);
  line-height: 1.65;
}

.newsflash-card-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 2px;
}

.ticker-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ticker-chip-muted {
  background: var(--color-background);
  color: var(--color-text-secondary);
}

.newsflash-cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--color-highlight);
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.newsflash-cta .material-icons-outlined {
  font-size: 18px;
}

.read-more-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background-color: var(--color-highlight);
  color: var(--color-text-on-highlight);
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--highlight-hover);
    color: var(--color-text-on-highlight);
  }
}

.secondary-read-button {
  background: var(--color-background);
  color: var(--color-text-primary);
  border: 1px solid var(--color-outline);

  &:hover {
    background: var(--color-surface);
    color: var(--color-text-primary);
  }
}

.detail-card {
  background-color: var(--color-surface);
  border: 1px solid var(--color-outline);
  border-radius: 8px;
  overflow: hidden;
}

.detail-hero {
  padding: 26px 28px 24px;
  border-bottom: 1px solid var(--color-outline);
  background: var(--color-background);
}

.detail-topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.detail-headline {
  max-width: 820px;
  margin: 0 0 12px;
  color: var(--color-primary);
  font-size: 30px;
  font-weight: 700;
  line-height: 1.25;
}

.detail-block {
  padding: 24px 28px;
  border-top: 1px solid var(--color-outline);
}

.detail-title {
  margin: 0 0 12px 0;
  color: var(--color-primary);
  font-size: 20px;
  font-weight: 600;
}

.detail-text {
  margin: 0;
  white-space: pre-line;
}

.market-context-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;

  div {
    padding: 12px;
    background: var(--color-background);
    border: 1px solid var(--color-outline);
    border-radius: 8px;
  }

  span {
    display: block;
    margin-bottom: 5px;
    color: var(--color-text-secondary);
    font-size: 12px;
  }
}

.market-context-text {
  margin-top: 4px;
}

.comparable-event {
  display: grid;
  gap: 6px;
  margin-top: 16px;
  padding: 14px;
  background: var(--color-background);
  border: 1px solid var(--color-outline);
  border-radius: 8px;

  span {
    color: var(--color-text-secondary);
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
  }

  strong {
    color: var(--color-primary);
  }

  p {
    margin: 0;
    color: var(--color-text-secondary);
    line-height: 1.55;
  }

  a {
    color: var(--color-highlight);
    font-weight: 700;
    text-decoration: none;
  }
}

.stocks-list {
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--color-outline);
}

.stock-item {
  padding: 16px 0;
  border-bottom: 1px solid var(--color-outline);

  &:last-child {
    border-bottom: 0;
  }
}

.stock-name {
  color: var(--color-text-primary);
  font-weight: 600;
}

.stock-detail-group + .stock-detail-group {
  margin-top: 12px;
}

.stock-detail-label {
  margin: 12px 0 6px 0;
  color: var(--color-text-secondary);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.stock-reason {
  margin: 8px 0 0 0;
}

.detail-disclaimer {
  margin: 0;
  padding: 18px 28px 22px;
  border-top: 1px solid var(--color-outline);
  background: var(--color-background);
  color: var(--color-text-secondary);
  font-size: 13px;
  line-height: 1.6;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 767px) {
  .informe-main-container {
    padding: 24px 16px 32px;
  }
}

@media (max-width: 640px) {
  .detail-hero,
  .detail-block,
  .detail-disclaimer,
  .content-state {
    padding: 20px;
  }

  .newsflash-item-shell {
    padding: 18px;
  }

  .informe-title {
    font-size: 20px;
  }

  .detail-topbar {
    flex-direction: column;
    align-items: flex-start;
  }

  .detail-headline {
    font-size: 24px;
  }

  .newsflash-card-footer {
    align-items: flex-start;
    flex-direction: column;
  }

  .market-context-grid {
    grid-template-columns: 1fr;
  }
}
</style>
